import { __rest, __spreadArray, __assign } from 'tslib';
import classnames from 'classnames';
import React, { useState, useCallback, Fragment, useEffect, useContext, useRef, useMemo } from 'react';
import { useTransition, config, animated } from '@react-spring/web';
import { createPortal } from 'react-dom';
import FocusLock, { AutoFocusInside } from 'react-focus-lock/UI';
import { sidecar } from 'use-sidecar';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { usePopper } from 'react-popper';

var DEFAULT_ELEMENT$z = 'section';
var Accordion = /*#__PURE__*/React.forwardRef(function AOAccordion(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$z : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.alwaysOpen,
      alwaysOpen = _c === void 0 ? false : _c,
      _d = _a.small,
      small = _d === void 0 ? false : _d,
      _f = _a.noBorder,
      noBorder = _f === void 0 ? false : _f,
      props = __rest(_a, ["as", "children", "className", "alwaysOpen", "small", "noBorder"]);

  var _g = useState([]),
      isOpenList = _g[0],
      setIsOpenList = _g[1];

  var handleClickOpenAll = useCallback(function (_e, id) {
    return setIsOpenList(function (currentState) {
      return currentState.includes(id) ? currentState.filter(function (accordionName) {
        return accordionName !== id;
      }) : __spreadArray(__spreadArray([], currentState, true), [id], false);
    });
  }, []);
  var handleClickOpenSingle = useCallback(function (_e, id) {
    return setIsOpenList(function (currentState) {
      return currentState.includes(id) ? [] : [id];
    });
  }, []);
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "accordion"
  }, props, {
    className: classnames(className),
    ref: ref
  }), React.Children.map(children, function (child, index) {
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.isValidElement(child) ? /*#__PURE__*/React.cloneElement(child, {
      className: classnames(index !== 0 && 'border-t-0', child.props.className),
      onClick: alwaysOpen ? handleClickOpenAll : handleClickOpenSingle,
      small: small,
      noBorder: noBorder,
      isOpenList: isOpenList
    }) : child);
  }));
});

var filterProps = function filterProps(componentProps, filterList) {
  return Object.keys(componentProps).reduce(function (newProps, prop) {
    var _a;

    return filterList.includes(prop) ? newProps : __assign(__assign({}, newProps), (_a = {}, _a[prop] = componentProps[prop], _a));
  }, {});
};

var DEFAULT_ELEMENT$y = 'div';
var AccordionContent = /*#__PURE__*/React.forwardRef(function AOAccordionContent(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$y : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      groupName = _a.groupName,
      inheritTextStyle = _a.inheritTextStyle,
      noPadding = _a.noPadding,
      props = __rest(_a, ["as", "children", "className", "isOpen", "groupName", "inheritTextStyle", "noPadding"]);

  return groupName ? /*#__PURE__*/React.createElement(ElementType, __assign({
    id: "".concat(groupName, "-content"),
    className: classnames(!isOpen && 'hidden', !inheritTextStyle && 'text-body', !noPadding && 'p-4', className),
    "data-aods": "accordion-content",
    "aria-labelledby": "".concat(groupName, "-header"),
    role: "region"
  }, filterProps(props, ['small', 'onClick']), {
    ref: ref
  }), children) : null;
});

var DEFAULT_ELEMENT$x = 'span';
var AoIcon = /*#__PURE__*/React.forwardRef(function AOIcon(_a, ref) {
  var _b;

  var className = _a.className,
      _c = _a.as,
      ElementType = _c === void 0 ? DEFAULT_ELEMENT$x : _c,
      icon = _a.icon,
      size = _a.size,
      labelProp = _a.label,
      props = __rest(_a, ["className", "as", "icon", "size", "label"]);

  var label = labelProp || props['aria-label'];
  var hasDescription = label || props['aria-labelledby'] || props['aria-describedby'];
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "icon"
  }, props, {
    className: classnames('ico', "ico-".concat(icon), (_b = {}, _b["ico-".concat(size)] = size, _b), className),
    "aria-label": label,
    "aria-hidden": !hasDescription || undefined,
    ref: ref
  }));
});

var DEFAULT_ELEMENT$w = 'div';
var AccordionHeader = /*#__PURE__*/React.forwardRef(function AOAccordionHeader(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$w : _b,
      children = _a.children,
      className = _a.className,
      _onClick = _a.onClick,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      groupName = _a.groupName,
      _d = _a.closeIcon,
      closeIcon = _d === void 0 ? 'chevron-up' : _d,
      _e = _a.openIcon,
      openIcon = _e === void 0 ? 'chevron-down' : _e,
      small = _a.small,
      icon = _a.icon,
      inheritTextStyle = _a.inheritTextStyle,
      _f = _a.padding,
      padding = _f === void 0 ? 'p-4' : _f,
      props = __rest(_a, ["as", "children", "className", "onClick", "isOpen", "groupName", "closeIcon", "openIcon", "small", "icon", "inheritTextStyle", "padding"]);

  return groupName ? /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "accordion-header",
    className: classnames(className)
  }, props, {
    ref: ref
  }), /*#__PURE__*/React.createElement("button", {
    id: "".concat(groupName, "-header"),
    className: classnames(!inheritTextStyle && (small ? 'text-title-sm' : 'text-title'), "bg-transparent w-full flex items-center justify-between text-left ".concat(padding, " focus:shadow-outline-blue focus:outline-none border-0")),
    type: "button",
    "aria-expanded": isOpen,
    onClick: function onClick(e) {
      return _onClick === null || _onClick === void 0 ? void 0 : _onClick(e, groupName);
    },
    "aria-controls": "".concat(groupName, "-content")
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center flex-grow"
  }, icon && /*#__PURE__*/React.createElement(AoIcon, {
    icon: icon,
    className: "mr-4 text-ongreen",
    "aria-hidden": "true",
    size: "2x"
  }), children), /*#__PURE__*/React.createElement(AoIcon, {
    className: "text-cta-action",
    icon: isOpen ? closeIcon : openIcon,
    "aria-hidden": "true",
    size: small ? 'lg' : 'xl'
  }))) : null;
});

var DEFAULT_ELEMENT$v = 'div';
var AccordionItem = /*#__PURE__*/React.forwardRef(function AOAccordionItem(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$v : _b,
      children = _a.children,
      className = _a.className,
      onClick = _a.onClick,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      _d = _a.isOpenList,
      isOpenList = _d === void 0 ? [] : _d,
      groupName = _a.groupName,
      _e = _a.small,
      small = _e === void 0 ? false : _e,
      _f = _a.noBorder,
      noBorder = _f === void 0 ? false : _f,
      props = __rest(_a, ["as", "children", "className", "onClick", "isOpen", "isOpenList", "groupName", "small", "noBorder"]);

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "accordion-item"
  }, props, {
    className: classnames(!noBorder && 'border border-l-0 border-r-0 border-gray-40', className),
    ref: ref
  }), React.Children.map(children, function (child) {
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.isValidElement(child) ? /*#__PURE__*/React.cloneElement(child, {
      onClick: onClick || child.props.onClick,
      isOpen: isOpenList.includes(groupName) || isOpen,
      groupName: groupName,
      small: small
    }) : child);
  }));
});

var DEFAULT_ELEMENT$u = 'nav';
var Breadcrumb = /*#__PURE__*/React.forwardRef(function AOBreadcrumb(_a, ref) {
  var children = _a.children,
      _b = _a["as"],
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$u : _b,
      _c = _a["aria-label"],
      ariaLabel = _c === void 0 ? 'Breadcrumbs' : _c,
      inheritTextStyle = _a.inheritTextStyle,
      dividerClassName = _a.dividerClassName,
      props = __rest(_a, ["children", 'as', 'aria-label', "inheritTextStyle", "dividerClassName"]);

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "breadcrumb"
  }, props, {
    ref: ref,
    "aria-label": ariaLabel
  }), /*#__PURE__*/React.createElement("ol", {
    className: classnames('flex flex-wrap items-center list-none', !inheritTextStyle && 'text-body')
  }, React.Children.map(children, function (child, index) {
    var isLast = index === React.Children.count(children) - 1;
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("li", {
      className: "flex items-center"
    }, /*#__PURE__*/React.isValidElement(child) ? /*#__PURE__*/React.cloneElement(child, {
      active: isLast
    }) : child, !isLast && /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true",
      className: dividerClassName || 'px-2 text-lg'
    }, "/")));
  })));
});

var DEFAULT_ELEMENT$t = 'a';
var BreadcrumbItem = /*#__PURE__*/React.forwardRef(function AOBreadcrumbItem(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$t : _b,
      active = _a.active,
      children = _a.children,
      className = _a.className,
      href = _a.href,
      props = __rest(_a, ["as", "active", "children", "className", "href"]);

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames('no-underline', active ? 'text-secondary font-bold' : 'text-link', className),
    "aria-current": active ? 'page' : undefined,
    href: href,
    "data-aods": "breadcrumb-item"
  }, props, {
    ref: ref
  }), children);
});

var BUTTON_VARIANTS = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  tertiary: 'btn-tertiary',
  link: 'bg-transparent border-none text-link p-0 min-h-0 min-w-0',
  white: 'btn-white',
  dark: 'btn-dark'
};
var DEFAULT_ELEMENT$s = 'button';
var Button = /*#__PURE__*/React.forwardRef(function AOButton(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$s : _b,
      children = _a.children,
      className = _a.className,
      color = _a.color,
      _c = _a.variant,
      variant = _c === void 0 ? 'primary' : _c,
      disabled = _a.disabled,
      _d = _a.lg,
      lg = _d === void 0 ? false : _d,
      _e = _a.icon,
      icon = _e === void 0 ? false : _e,
      _f = _a.fullWidth,
      fullWidth = _f === void 0 ? false : _f,
      props = __rest(_a, ["as", "children", "className", "color", "variant", "disabled", "lg", "icon", "fullWidth"]);

  var isButtonElement = ElementType === 'button';
  var isLink = 'href' in props || 'to' in props;
  var buttonVariant = color || variant;
  return /*#__PURE__*/React.createElement(ElementType // eslint-disable-next-line no-undefined
  , __assign({
    // eslint-disable-next-line no-undefined
    type: isButtonElement ? 'button' : undefined,
    disabled: isButtonElement && disabled,
    "aria-disabled": disabled,
    // eslint-disable-next-line no-undefined
    role: !isLink && !isButtonElement ? 'button' : undefined,
    className: classnames('btn', icon && 'btn-icon', fullWidth && 'w-full justify-center', lg && 'btn-lg', disabled ? 'btn-inactive' : BUTTON_VARIANTS[buttonVariant], className),
    "data-aods": "button"
  }, props, {
    ref: ref
  }), children);
});

var DEFAULT_ELEMENT$r = 'div';
var Card = /*#__PURE__*/React.forwardRef(function AOCard(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$r : _b,
      children = _a.children,
      className = _a.className,
      flex = _a.flex,
      inheritTextStyle = _a.inheritTextStyle,
      noBorder = _a.noBorder,
      noPadding = _a.noPadding,
      raised = _a.raised,
      square = _a.square,
      state = _a.state,
      props = __rest(_a, ["as", "children", "className", "flex", "inheritTextStyle", "noBorder", "noPadding", "raised", "square", "state"]);

  var border = {
    'border-gray-40': !state,
    'border-error': state === 'error',
    'border-attention': state === 'attention',
    'border-success': state === 'success',
    'border-highlight': state === 'highlight'
  };
  var background = {
    'bg-white': !state,
    'bg-error': state === 'error',
    'bg-attention': state === 'attention',
    'bg-success': state === 'success',
    'bg-highlight': state === 'highlight'
  };
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames('border border-solid text-body', !square && 'rounded', !inheritTextStyle && 'text-body', flex && 'flex flex-col', !noPadding && 'p-4 lg:p-6', noBorder ? 'border-transparent' : border, raised === true && 'shadow-4dp', background, className // TODO: opt-in hover effect with 8dp shadow for flat / 12dp for raised
    ),
    "data-aods": "card"
  }, props, {
    ref: ref
  }), children);
});

var DEFAULT_ELEMENT$q = 'hr';
var CardDivider = /*#__PURE__*/React.forwardRef(function AOCardDivider(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$q : _b,
      children = _a.children,
      className = _a.className,
      state = _a.state,
      noBorder = _a.noBorder,
      noPadding = _a.noPadding,
      noMargin = _a.noMargin,
      props = __rest(_a, ["as", "children", "className", "state", "noBorder", "noPadding", "noMargin"]);

  var border = {
    'border-gray-40': !state,
    'border-error': state === 'error',
    'border-attention': state === 'attention',
    'border-success': state === 'success',
    'border-highlight': state === 'highlight'
  };
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames('border-0 h-0 border-b border-solid', noBorder ? 'border-transparent' : 'border-gray-40', !noPadding && '-mx-4 lg:-mx-6', !noMargin && (noBorder ? 'my-2 lg:my-3' : 'my-4 lg:my-6'), border, className),
    "data-aods": "card-divider"
  }, props, {
    ref: ref
  }), children);
});

var toggleTypeToInputType = function toggleTypeToInputType(type) {
  if (type === 'radio-button') return 'radio';
  return type;
};

var DEFAULT_ELEMENT$p = 'input';
var ToggleBase = /*#__PURE__*/React.forwardRef(function AOToggleBase(_a, ref) {
  var _b = _a.align,
      align = _b === void 0 ? 'left' : _b,
      hideToggle = _a.hideToggle,
      _c = _a.as,
      ElementType = _c === void 0 ? DEFAULT_ELEMENT$p : _c,
      className = _a.className,
      children = _a.children,
      id = _a.id,
      _d = _a.fullWidth,
      fullWidth = _d === void 0 ? false : _d,
      _e = _a.labelProps,
      labelProps = _e === void 0 ? {} : _e,
      pill = _a.pill,
      padded = _a.padded,
      bodyClassName = _a.bodyClassName,
      state = _a.state,
      type = _a.type,
      inheritTextStyle = _a.inheritTextStyle,
      disabled = _a.disabled,
      props = __rest(_a, ["align", "hideToggle", "as", "className", "children", "id", "fullWidth", "labelProps", "pill", "padded", "bodyClassName", "state", "type", "inheritTextStyle", "disabled"]);

  var hasError = !disabled && state === 'error';
  return (
    /*#__PURE__*/
    // Input is nested, can't detect ElementType
    // eslint-disable-next-line jsx-a11y/label-has-for
    React.createElement("label", __assign({}, labelProps, {
      htmlFor: id,
      className: classnames(type, fullWidth && 'flex', pill && "".concat(type, "-pill"), padded && !pill && "".concat(type, "-padded"), inheritTextStyle && "".concat(type, "-inherit-text"), align === 'right' && "".concat(type, "-reverse"), hideToggle && "".concat(type, "-no-toggle"), className)
    }), /*#__PURE__*/React.createElement(ElementType, __assign({
      "data-aods": type
    }, props, {
      id: id,
      type: toggleTypeToInputType(type),
      ref: ref,
      className: "".concat(type, "_input"),
      disabled: disabled
    })), /*#__PURE__*/React.createElement("span", {
      className: classnames("".concat(type, "_body"), bodyClassName, pill && hasError && 'bg-error border-error')
    }, !hideToggle && /*#__PURE__*/React.createElement("span", {
      className: classnames("".concat(type, "_icon-container"))
    }, /*#__PURE__*/React.createElement("span", {
      className: classnames("".concat(type, "_icon"), hasError && 'text-error border-error')
    })), /*#__PURE__*/React.createElement("span", {
      className: classnames("".concat(type, "_label"), hasError && 'text-error')
    }, children)))
  );
});

var Checkbox = /*#__PURE__*/React.forwardRef(function AOCheckbox(props, ref) {
  return /*#__PURE__*/React.createElement(ToggleBase, __assign({}, props, {
    type: "checkbox",
    ref: ref
  }));
});

var FieldSet = /*#__PURE__*/React.forwardRef(function AOFieldSet(_a, ref) {
  var className = _a.className,
      props = __rest(_a, ["className"]);

  return /*#__PURE__*/React.createElement("fieldset", __assign({
    "data-aods": "fieldset"
  }, props, {
    className: classnames(className, 'inline-block border-0 p-0 m-0 min-w-0'),
    ref: ref
  }));
});

var Legend = /*#__PURE__*/React.forwardRef(function AOLegend(_a, ref) {
  var _b;

  var className = _a.className,
      style = _a.style,
      _c = _a.display,
      display = _c === void 0 ? false : _c,
      _d = _a.noMargin,
      noMargin = _d === void 0 ? false : _d,
      _e = _a.srOnly,
      srOnly = _e === void 0 ? false : _e,
      inheritTextStyle = _a.inheritTextStyle,
      props = __rest(_a, ["className", "style", "display", "noMargin", "srOnly", "inheritTextStyle"]);

  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("legend", __assign({
    "data-aods": "legend"
  }, props, {
    className: srOnly ? 'sr-only' : classnames(className, 'table w-full', display && (_b = {
      'text-display': display && display === 'md'
    }, _b["text-display-".concat(display)] = display && display !== 'md', _b), !inheritTextStyle && !display && 'text-body', !noMargin && (display ? 'mb-4' : 'mb-2')),
    style: __assign(__assign({}, style), {
      float: 'left'
    }),
    ref: ref
  })), /*#__PURE__*/React.createElement("span", {
    className: "block",
    style: {
      clear: 'both'
    }
  }));
});

var DEFAULT_ELEMENT$o = FieldSet;
var ToggleBaseGroup = /*#__PURE__*/React.forwardRef(function AOToggleBaseGroup(_a, ref) {
  var children = _a.children,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$o : _b,
      _c = _a.align,
      align = _c === void 0 ? 'left' : _c,
      className = _a.className,
      _d = _a.flush,
      flush = _d === void 0 ? false : _d,
      _e = _a.fullWidth,
      fullWidth = _e === void 0 ? false : _e,
      hideLabel = _a.hideLabel,
      _f = _a.hideToggle,
      hideToggle = _f === void 0 ? false : _f,
      _g = _a.inheritTextStyle,
      inheritTextStyle = _g === void 0 ? false : _g,
      inline = _a.inline,
      label = _a.label,
      name = _a.name,
      _h = _a.padded,
      padded = _h === void 0 ? false : _h,
      _j = _a.pill,
      pill = _j === void 0 ? false : _j,
      state = _a.state,
      props = __rest(_a, ["children", "as", "align", "className", "flush", "fullWidth", "hideLabel", "hideToggle", "inheritTextStyle", "inline", "label", "name", "padded", "pill", "state"]);

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames(className, fullWidth && 'w-full'),
    ref: ref,
    "data-aods": "toggle-base-group"
  }, props), /*#__PURE__*/React.createElement(Legend, {
    className: classnames(!hideLabel && 'mb-2'),
    srOnly: hideLabel,
    inheritTextStyle: inheritTextStyle
  }, label), /*#__PURE__*/React.createElement("div", {
    className: classnames(fullWidth && 'w-full', inline && 'flex', !flush && "space-".concat(inline ? 'x' : 'y', "-2"), flush && pill && "-space-".concat(inline ? 'x' : 'y', "-px"))
  }, React.Children.map(children, function (child, i) {
    var _a;

    if (! /*#__PURE__*/React.isValidElement(child)) return child;
    var bodyClassName = child.props.bodyClassName;

    var toggleProps = __assign(__assign({
      name: name,
      pill: pill,
      padded: padded,
      inheritTextStyle: inheritTextStyle,
      align: align,
      hideToggle: hideToggle,
      state: state
    }, child.props), {
      fullWidth: true,
      bodyClassName: classnames(bodyClassName, (_a = {}, _a["rounded-".concat(inline ? 'r' : 'b', "-none")] = flush && i < React.Children.count(children) - 1, _a["rounded-".concat(inline ? 'l' : 't', "-none")] = flush && i > 0, _a))
    });

    return /*#__PURE__*/React.cloneElement(child, toggleProps);
  })));
});

var CheckboxGroup = function CheckboxGroup(_a) {
  var children = _a.children,
      props = __rest(_a, ["children"]);

  return /*#__PURE__*/React.createElement(ToggleBaseGroup, __assign({}, props), children);
};

var DEFAULT_ELEMENT$n = 'div';
var Container = /*#__PURE__*/React.forwardRef(function AOContainer(_a, ref) {
  var children = _a.children,
      className = _a.className,
      noMargin = _a.noMargin,
      breakpoint = _a.breakpoint,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$n : _b,
      props = __rest(_a, ["children", "className", "noMargin", "breakpoint", "as"]);

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames(breakpoint ? ["".concat(breakpoint, ":container"), !noMargin && "".concat(breakpoint, ":p-4")] : ['container', !noMargin && 'p-2 sm:p-4'], 'mx-auto', className),
    "data-aods": "container"
  }, props, {
    ref: ref
  }), children);
});

var defaultConfig = {
  language: 'en',
  // Default to false so behaviour with no provider is expected client side use
  isSSR: false
};
var DesignSystemContext = /*#__PURE__*/React.createContext(defaultConfig);

var DesignSystemProvider = function DesignSystemProvider(_a) {
  var children = _a.children,
      userConfig = __rest(_a, ["children"]);

  var _b = useState(Object.assign({}, defaultConfig, // Override default so initial value is true which is overriden by hook on client-side
  {
    isSSR: true
  }, userConfig)),
      config = _b[0],
      setConfig = _b[1];

  useEffect(function () {
    setConfig(function (currentConfig) {
      return __assign(__assign({}, currentConfig), {
        isSSR: false
      });
    });
  }, []);
  return /*#__PURE__*/React.createElement(DesignSystemContext.Provider, {
    value: config
  }, children);
};

var DEFAULT_ELEMENT$m = 'div';
var Overlay = /*#__PURE__*/React.forwardRef(function AOOverlay(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$m : _b,
      isOpen = _a.isOpen,
      props = __rest(_a, ["children", "className", "as", "isOpen"]);

  var transitions = useTransition(isOpen, __assign({
    from: {
      opacity: 0
    },
    enter: {
      opacity: 1
    },
    leave: {
      opacity: 0
    }
  }, config.stiff));
  var ElementType = animated[as];
  return /*#__PURE__*/React.createElement(Fragment, null, transitions(function (style, item) {
    return item && /*#__PURE__*/React.createElement(ElementType, __assign({
      className: classnames('bg-overlay fixed inset-0', isOpen ? 'pointer-events-auto' : 'pointer-events-none', className),
      "data-aods": "overlay"
    }, props, {
      style: __assign({}, style),
      ref: ref
    }), children);
  }));
});

function useBodyScrollLock(targetRef, isLocked) {
  useEffect(function () {
    var options = {
      reserveScrollBarGap: true
    };

    if (targetRef === null || targetRef === void 0 ? void 0 : targetRef.current) {
      if (isLocked) {
        disableBodyScroll(targetRef.current, options);
      } else {
        enableBodyScroll(targetRef.current);
      }
    }

    return function () {
      return clearAllBodyScrollLocks();
    };
  }, [isLocked, targetRef]);
}

function useClickAway(ref, onClickAway) {
  useEffect(function () {
    if (!document || !ref.current || !onClickAway) return;

    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        onClickAway === null || onClickAway === void 0 ? void 0 : onClickAway(event);
      }
    }

    document.addEventListener('mousedown', handleClickOutside); // eslint-disable-next-line consistent-return

    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, onClickAway]);
}

function useConfig() {
  return useContext(DesignSystemContext);
}

function useForwardedRef(r) {
  var innerRef = useRef(null);
  useEffect(function () {
    if (!r) return;

    if (typeof r === 'function') {
      r(innerRef.current);
    } else {
      r.current = innerRef.current;
    }
  }, [r]);
  return innerRef;
}

function useKeyDown(_a) {
  var isEnabled = _a.isEnabled,
      key = _a.key,
      onKeyDown = _a.onKeyDown;
  var handleKeyDown = useCallback(function (e) {
    if (key && (Array.isArray(key) ? key.includes(e.key) : e.key !== key)) {
      return;
    }

    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
  }, [key, onKeyDown]);
  useEffect(function () {
    if (isEnabled && onKeyDown) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEnabled, handleKeyDown, onKeyDown]);
}

function useTranslation(language, translations) {
  var t = useMemo(function () {
    return translations[language];
  }, [language, translations]);
  return useCallback(function (key) {
    var args = [];

    for (var _i = 1; _i < arguments.length; _i++) {
      args[_i - 1] = arguments[_i];
    }

    var value = t[key];
    return value.apply(void 0, args);
  }, [t]);
}

var translations$3 = {
  en: {
    close: function close() {
      return 'Close';
    }
  },
  de: {
    close: function close() {
      return 'Schließen';
    }
  }
};

var DEFAULT_ELEMENT$l = 'div';
var animationProps = {
  bottom: {
    from: {
      transform: 'translateY(100%)',
      maxHeight: '85%'
    },
    enter: {
      transform: 'translateY(0%)'
    },
    leave: {
      transform: 'translateY(100%)'
    }
  },
  left: {
    from: {
      transform: 'translateX(-100%)'
    },
    enter: {
      transform: 'translateX(0%)'
    },
    leave: {
      transform: 'translateX(-100%)'
    }
  },
  right: {
    from: {
      transform: 'translateX(100%)'
    },
    enter: {
      transform: 'translateX(0%)'
    },
    leave: {
      transform: 'translateX(100%)'
    }
  },
  top: {
    from: {
      transform: 'translateY(-100%)',
      maxHeight: '85%'
    },
    enter: {
      transform: 'translateY(0%)'
    },
    leave: {
      transform: 'translateY(-100%)'
    }
  }
};
var FocusLockSidecar$1 = /*#__PURE__*/sidecar(function () {
  return import(
  /* webpackPrefetch: true */
  'react-focus-lock/sidecar');
});
var Drawer = /*#__PURE__*/React.forwardRef(function AODrawer(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$l : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      onClose = _a.onClose,
      _d = _a.edge,
      edge = _d === void 0 ? 'bottom' : _d,
      _e = _a.hideCloseButton,
      hideCloseButton = _e === void 0 ? false : _e,
      _f = _a.closeIcon,
      closeIcon = _f === void 0 ? 'close' : _f,
      title = _a.title,
      titleReverse = _a.titleReverse,
      _g = _a.persist,
      persist = _g === void 0 ? false : _g,
      portalTarget = _a.portalTarget,
      props = __rest(_a, ["as", "children", "className", "isOpen", "onClose", "edge", "hideCloseButton", "closeIcon", "title", "titleReverse", "persist", "portalTarget"]);

  var _h = useConfig(),
      language = _h.language,
      isSSR = _h.isSSR;

  var t = useTranslation(language, translations$3);
  var transitions = useTransition(isOpen, __assign(__assign(__assign({}, animationProps[edge]), config.stiff), {
    expires: !persist
  })); // TODO: Get element type from `as`

  var innerRef = useForwardedRef(ref);
  useBodyScrollLock(innerRef, isOpen);
  useClickAway(innerRef, isOpen ? onClose : undefined);
  var ElementType = animated[as];
  useKeyDown({
    isEnabled: isOpen,
    key: 'Escape',
    onKeyDown: onClose
  });
  var container = portalTarget || document && document.body;
  return !isSSR && container ? /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(Fragment, null, transitions(function (style, item) {
    return item && /*#__PURE__*/React.createElement("div", {
      className: classnames('fixed inset-0 z-50', !isOpen && 'pointer-events-none')
    }, /*#__PURE__*/React.createElement(Overlay, {
      isOpen: isOpen,
      "data-testid": "drawer-overlay"
    }), /*#__PURE__*/React.createElement(ElementType // Coerce type to override legacy ref conflict
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    , __assign({
      // Coerce type to override legacy ref conflict
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref: innerRef,
      style: __assign({}, style),
      className: classnames('absolute z-50 p-4 bg-white shadow-2dp overflow-auto', {
        'w-full bottom-0 rounded-t-lg': edge === 'bottom',
        'h-full w-11/12 md:w-3/4 lg:w-1/2 xl:w-5/12 left-0': edge === 'left',
        'h-full w-11/12 md:w-3/4 lg:w-1/2 xl:w-5/12 right-0': edge === 'right',
        'w-full top-0 rounded-b-lg': edge === 'top'
      }, className),
      role: "dialog",
      "data-aods": "drawer"
    }, props), /*#__PURE__*/React.createElement(FocusLock, {
      disabled: !isOpen,
      returnFocus: true,
      sideCar: FocusLockSidecar$1
    }, /*#__PURE__*/React.createElement(AutoFocusInside, null, /*#__PURE__*/React.createElement("div", {
      className: classnames('flex items-center', titleReverse ? 'flex-row-reverse justify-end' : 'justify-between')
    }, /*#__PURE__*/React.isValidElement(title) ? /*#__PURE__*/React.cloneElement(title, {
      className: classnames('m-0', title.props.className)
    }) : title, !hideCloseButton && /*#__PURE__*/React.createElement(Button, {
      className: classnames(titleReverse ? 'mr-4' : 'ml-auto'),
      icon: true,
      color: "tertiary",
      onClick: onClose,
      "aria-label": t('close')
    }, /*#__PURE__*/React.createElement(AoIcon, {
      icon: closeIcon,
      size: "xl"
    }))), children))));
  })), container) : null;
});

var translations$2 = {
  en: {
    close: function close() {
      return 'Close';
    }
  },
  de: {
    close: function close() {
      return 'Schließen';
    }
  }
};

var DEFAULT_ELEMENT$k = 'div';
var FocusLockSidecar = /*#__PURE__*/sidecar(function () {
  return import(
  /* webpackPrefetch: true */
  'react-focus-lock/sidecar');
});
var Modal = /*#__PURE__*/React.forwardRef(function AOModal(_a, ref) {
  var children = _a.children,
      _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$k : _b,
      className = _a.className,
      hideCloseButton = _a.hideCloseButton,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      disableCloseHelpers = _a.disableCloseHelpers,
      onClose = _a.onClose,
      _d = _a.size,
      size = _d === void 0 ? 'md' : _d,
      portalTarget = _a.portalTarget,
      title = _a.title,
      inheritTextStyle = _a.inheritTextStyle,
      _e = _a.persist,
      persist = _e === void 0 ? false : _e,
      props = __rest(_a, ["children", "as", "className", "hideCloseButton", "isOpen", "disableCloseHelpers", "onClose", "size", "portalTarget", "title", "inheritTextStyle", "persist"]);

  var _f = useConfig(),
      language = _f.language,
      isSSR = _f.isSSR;

  var t = useTranslation(language, translations$2);
  var transitions = useTransition(isOpen, __assign(__assign({
    from: {
      transform: 'scale(0.75)',
      opacity: 0
    },
    enter: {
      transform: 'scale(1)',
      opacity: 1
    },
    leave: {
      transform: 'scale(0.75)',
      opacity: 0
    }
  }, config.stiff), {
    expires: !persist
  }));
  var ElementType = animated[as];
  var modalContentRef = useForwardedRef(ref);
  var previousFocusRef = useRef(null);
  useClickAway(modalContentRef, !disableCloseHelpers && isOpen ? onClose : undefined);
  useEffect(function () {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
    } else if (previousFocusRef.current && previousFocusRef.current.focus) {
      previousFocusRef.current.focus();
    }
  }, [isOpen]);
  useBodyScrollLock(modalContentRef, isOpen);
  useKeyDown({
    isEnabled: !disableCloseHelpers && isOpen,
    key: 'Escape',
    onKeyDown: onClose
  });
  var container = portalTarget || document && document.body;
  return !isSSR && container ? /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(Fragment, null, transitions(function (style, item) {
    return item &&
    /*#__PURE__*/
    // Use fixed z-index rather than class to display above header
    React.createElement("div", {
      className: classnames('fixed inset-0', !isOpen && 'pointer-events-none'),
      style: {
        zIndex: 200
      }
    }, /*#__PURE__*/React.createElement(Overlay, {
      isOpen: isOpen,
      "data-testid": "modal-overlay"
    }), /*#__PURE__*/React.createElement("div", {
      className: "fixed inset-0 flex flex-col items-center p-4 overflow-y-auto"
    }, /*#__PURE__*/React.createElement(ElementType, __assign({
      className: classnames('bg-white rounded-lg relative p-4 lg:p-6 text-body shadow-2dp my-auto max-w-full overflow-x-auto', !inheritTextStyle && 'text-body', size !== 'auto' && "max-w-screen-".concat(size, " w-full"), className),
      // Coerce type to override legacy ref conflict
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref: modalContentRef,
      role: "dialog",
      "aria-modal": true,
      style: __assign({}, style),
      "data-aods": "modal"
    }, props), /*#__PURE__*/React.createElement(FocusLock, {
      sideCar: FocusLockSidecar,
      disabled: !isOpen
    }, /*#__PURE__*/React.createElement(AutoFocusInside, null, (title || !hideCloseButton) && /*#__PURE__*/React.createElement("div", {
      className: "flex items-center justify-between pb-1"
    }, /*#__PURE__*/React.isValidElement(title) && /*#__PURE__*/React.cloneElement(title, {
      className: classnames('m-0', title.props.className)
    }), !hideCloseButton && /*#__PURE__*/React.createElement(Button, {
      className: "ml-auto",
      variant: "tertiary",
      icon: true,
      onClick: onClose,
      "aria-label": t('close')
    }, /*#__PURE__*/React.createElement(AoIcon, {
      icon: "close",
      size: "xl"
    }))), children)))));
  })), container) : null;
});

var DEFAULT_ELEMENT$j = 'span';
var Tag = /*#__PURE__*/React.forwardRef(function AOTag(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$j : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.color,
      color = _c === void 0 ? 'core' : _c,
      square = _a.square,
      border = _a.border,
      inheritTextStyle = _a.inheritTextStyle,
      _d = _a.variant,
      variant = _d === void 0 ? 'pill' : _d,
      inverse = _a.inverse,
      noPadding = _a.noPadding,
      noFlex = _a.noFlex,
      props = __rest(_a, ["as", "children", "className", "color", "square", "border", "inheritTextStyle", "variant", "inverse", "noPadding", "noFlex"]);

  var isCustomColor = color === 'custom';
  var isBare = variant === 'bare';
  var isPill = variant === 'pill';
  var isUnderline = variant === 'underline';
  var bgClassNameSuffix = inverse ? '-contrast' : '';
  var textClassNameSuffix = inverse ? '' : "-contrast";
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames(className, 'inline-block border border-solid', !inheritTextStyle && ['text-body-sm', !isBare && 'font-bold'], !isCustomColor && [border ? "border-ui-".concat(color, "-accent") : 'border-transparent', !inheritTextStyle && "text-ui-".concat(color).concat(textClassNameSuffix), isPill && "bg-ui-".concat(color).concat(bgClassNameSuffix)], (isBare || isCustomColor && !border) && 'border-transparent', !isBare && !square && 'rounded-full', isUnderline && 'inline-flex flex-col', !noPadding && 'py-2 px-3'),
    ref: ref,
    "data-aods": "tag"
  }, props), /*#__PURE__*/React.createElement("span", {
    className: classnames(!noFlex && 'flex items-center')
  }, children), isUnderline && /*#__PURE__*/React.createElement("span", {
    className: classnames('h-1 rounded-full w-full inline-block mt-1', "bg-ui-".concat(color).concat(textClassNameSuffix))
  }));
});

var translations$1 = {
  en: {
    energyRating: function energyRating(r) {
      return "".concat(r ? "".concat(r, " ") : '', "Energy Rating");
    }
  },
  de: {
    energyRating: function energyRating(r) {
      return "".concat(r ? "".concat(r, " ") : '', "Energieeffizienzklasse");
    }
  }
};

var SCALE = ['A+++', 'A++', 'A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];
var ENDPOINTS = {
  beta: 'https://design-system-beta.fe.ao-qa.com/assets/labels',
  staging: 'https://staging-assets.ao-qa.com/design-system/assets/labels',
  production: 'https://assets.ao.com/design-system/assets/labels'
};
var mapRatingToBackgroundColor = function mapRatingToBackgroundColor(ratings, highestRating) {
  var lowestRatingIndex = Math.max.apply(Math, ratings.map(function (r) {
    return SCALE.indexOf(r);
  }));
  var highestRatingIndex = SCALE.indexOf(highestRating);
  var defaultColor = '#EE1B24';

  if (lowestRatingIndex === -1 || highestRatingIndex === -1) {
    return defaultColor;
  }

  var rank = lowestRatingIndex - highestRatingIndex;

  switch (rank) {
    case 0:
      return '#00A751';

    case 1:
      return '#4DBC35';

    case 2:
      return '#B6DA18';

    case 3:
      return '#FEF100';

    case 4:
      return '#FBB20B';

    case 5:
      return '#F25D1C';

    default:
      return defaultColor;
  }
};
var DEFAULT_ELEMENT$i = 'a';
var EnergyLabel = /*#__PURE__*/React.forwardRef(function AOEnergyLabel(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$i : _b,
      children = _a.children,
      className = _a.className,
      rating = _a.rating,
      _c = _a.highestRating,
      highestRating = _c === void 0 ? 'A+++' : _c,
      modalProps = _a.modalProps,
      _d = _a.use2020Label,
      use2020Label = _d === void 0 ? false : _d,
      _e = _a.environment,
      environment = _e === void 0 ? 'production' : _e,
      _f = _a.reverse,
      reverse = _f === void 0 ? false : _f,
      props = __rest(_a, ["as", "children", "className", "rating", "highestRating", "modalProps", "use2020Label", "environment", "reverse"]);

  var language = useConfig().language;
  var t = useTranslation(language, translations$1);

  var _g = useState(false),
      isModalOpen = _g[0],
      setModalOpen = _g[1];

  var handleClick = useCallback(function (e) {
    setModalOpen(true);
    e.preventDefault();
  }, []);
  var handleModalClose = useCallback(function () {
    setModalOpen(false);
  }, []);
  var labelSrc = props.href || props.to;
  var linkProps = {};

  if (labelSrc) {
    linkProps = {
      target: '_blank',
      rel: 'noopener noreferrer',
      onClick: handleClick
    };
  }

  var ratings = useMemo(function () {
    return Array.isArray(rating) ? rating : [rating];
  }, [rating]);
  var ratingsString = ratings.join('/');
  var backgroundColor = mapRatingToBackgroundColor(ratings, use2020Label ? 'A' : highestRating);
  var ElementType = as === DEFAULT_ELEMENT$i && !labelSrc ? 'strong' : as;
  return /*#__PURE__*/React.createElement(Fragment, null, use2020Label ? /*#__PURE__*/React.createElement(ElementType, __assign({}, props, {
    className: className
  }, linkProps, {
    "data-aods": "energy-label"
  }, props), /*#__PURE__*/React.createElement("img", {
    src: "".concat(ENDPOINTS[environment], "/energy-label.svg?rating=").concat(ratings.join(','), "&reverse=").concat(reverse.toString()),
    alt: t('energyRating', ratingsString),
    style: {
      height: '2em',
      width: 'auto'
    }
  })) : /*#__PURE__*/React.createElement(Tag, __assign({
    "data-aods": "energy-label"
  }, props, {
    as: as,
    color: "custom",
    square: true,
    className: classnames('border-none text-base text-white no-underline relative leading-none', className),
    ref: ref,
    style: {
      backgroundColor: backgroundColor
    }
  }, linkProps), ratingsString, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, t('energyRating')), /*#__PURE__*/React.createElement("span", {
    className: classnames('absolute top-0 triangle triangle-offset', reverse ? 'triangle-left' : 'triangle-right'),
    style: {
      color: backgroundColor
    },
    "aria-hidden": true
  }), children), labelSrc && /*#__PURE__*/React.createElement(Modal, __assign({}, modalProps, {
    isOpen: isModalOpen,
    onClose: handleModalClose,
    size: "auto"
  }), /*#__PURE__*/React.createElement("img", {
    src: labelSrc,
    alt: t('energyRating', ratings.join('/')),
    className: "max-w-full"
  })));
});

var DEFAULT_ELEMENT$h = 'div';
var Field = /*#__PURE__*/React.forwardRef(function AOField(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$h : _b,
      children = _a.children,
      className = _a.className,
      name = _a.name,
      state = _a.state,
      props = __rest(_a, ["as", "children", "className", "name", "state"]);

  var inputId = "".concat(name, "-input");
  var messageId = "".concat(name, "-message");
  var renderProps = {
    labelProps: {
      htmlFor: inputId
    },
    inputProps: {
      name: name,
      'id': inputId,
      'aria-describedby': messageId,
      state: state
    },
    messageProps: {
      id: messageId,
      state: state
    }
  };
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "field"
  }, props, {
    className: classnames('flex flex-col max-w-md space-y-1 mb-4', className),
    ref: ref
  }), children(renderProps));
});

// TODO: Get from Tokens pkg
var BREAKPOINTS = ['def', 'sm', 'md', 'lg', 'xl'];

var getBreakpointVariantClassName = function getBreakpointVariantClassName(bp, value) {
  if (bp === 'def') return "".concat(value);
  return "".concat(bp, ":").concat(value);
};
var parseResponsiveProp = (function (propValue, transform) {
  if (transform === void 0) {
    transform = function transform(value) {
      return "".concat(value);
    };
  }

  if (Array.isArray(propValue)) {
    return propValue.map(function (value, index) {
      if (value === null) return false;
      var bp = BREAKPOINTS[index];
      return getBreakpointVariantClassName(bp, transform(value));
    }).filter(function (value) {
      return !!value;
    });
  } else if (typeof propValue === 'object') {
    return Object.keys(propValue).map(function (bp) {
      var value = propValue[bp];
      return getBreakpointVariantClassName(bp, transform(value));
    });
  }

  return [transform(propValue)];
});

var DEFAULT_ELEMENT$g = 'div';
var Grid = /*#__PURE__*/React.forwardRef(function AOGrid(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$g : _b,
      noGutter = _a.noGutter,
      reverse = _a.reverse,
      column = _a.column,
      equalHeight = _a.equalHeight,
      vAlign = _a.vAlign,
      hAlign = _a.hAlign,
      noWrap = _a.noWrap,
      props = __rest(_a, ["children", "className", "as", "noGutter", "reverse", "column", "equalHeight", "vAlign", "hAlign", "noWrap"]);

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames('flex', !noWrap && 'flex-wrap', !noGutter && '-m-2', column && parseResponsiveProp(column, function () {
      return 'flex-col';
    }), reverse && parseResponsiveProp(reverse, function () {
      return "flex-".concat(column ? 'col' : 'row', "-reverse");
    }), column ? [hAlign && "items-".concat(hAlign), vAlign && "justify-".concat(vAlign)] : [vAlign && "items-".concat(vAlign), hAlign && "justify-".concat(hAlign)], className),
    "data-aods": "grid"
  }, props, {
    ref: ref
  }), noGutter ? children : React.Children.map(children, function (child) {
    return /*#__PURE__*/React.isValidElement(child) ? /*#__PURE__*/React.cloneElement(child, {
      className: classnames('p-2', {
        flex: equalHeight
      }, child.props.className)
    }) : child;
  }));
});

var DEFAULT_ELEMENT$f = 'div';

var spanToClassName = function spanToClassName(cols) {
  switch (cols) {
    case 'auto':
      return 'flex-grow';

    case 'inline':
      return 'w-auto';

    case 12:
      return 'w-full';

    default:
      return "w-".concat(cols, "/12");
  }
};

var GridItem = /*#__PURE__*/React.forwardRef(function AOGridItem(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$f : _b,
      span = _a.span,
      props = __rest(_a, ["children", "className", "as", "span"]);

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames(span ? parseResponsiveProp(span, spanToClassName) : 'flex-grow', className)
  }, props, {
    "data-aods": "grid-item",
    ref: ref
  }), children);
});

var getInputTheme = (function (state, isDisabled) {
  if (isDisabled === void 0) {
    isDisabled = false;
  }

  var border = classnames({
    'border-gray-40': isDisabled || !state,
    'border-success': !isDisabled && state === 'success',
    'border-attention': !isDisabled && state === 'attention',
    'border-error': !isDisabled && state === 'error',
    'border-highlight': !isDisabled && state === 'highlight'
  });
  var background = classnames({
    'bg-gray-20': isDisabled,
    'bg-success': !isDisabled && state === 'success',
    'bg-attention': !isDisabled && state === 'attention',
    'bg-error': !isDisabled && state === 'error',
    'bg-highlight': !isDisabled && state === 'highlight'
  });
  var color = classnames({
    'text-gray-60': isDisabled,
    'text-cta-main': !isDisabled && state === 'success',
    'text-rating': !isDisabled && state === 'attention',
    'text-error': !isDisabled && state === 'error',
    'text-ice': !isDisabled && state === 'highlight'
  });
  return {
    border: border,
    background: background,
    color: color
  };
});

var DEFAULT_ELEMENT$e = 'input';
var Input = /*#__PURE__*/React.forwardRef(function AOInput(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$e : _b,
      className = _a.className,
      state = _a.state,
      disabled = _a.disabled,
      _c = _a.type,
      type = _c === void 0 ? 'text' : _c,
      _d = _a.seamless,
      seamless = _d === void 0 ? false : _d,
      _e = _a.fullWidth,
      fullWidth = _e === void 0 ? false : _e,
      inheritTextStyle = _a.inheritTextStyle,
      props = __rest(_a, ["as", "className", "state", "disabled", "type", "seamless", "fullWidth", "inheritTextStyle"]);

  var _f = getInputTheme(state, disabled),
      border = _f.border,
      background = _f.background;

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames('p-3 text-body leading-snug rounded placeholder-gray focus:outline-none appearance-none', !inheritTextStyle && 'text-body', fullWidth && 'w-full', seamless ? 'border-0 bg-transparent' : 'border border-solid focus:shadow-outline-blue', !seamless && [border, background], className),
    disabled: disabled,
    "aria-invalid": state === 'error' || undefined,
    "data-aods": "input"
  }, props, {
    ref: ref,
    type: type
  }));
});

var DEFAULT_ELEMENT$d = 'div';
var InputContainer = /*#__PURE__*/React.forwardRef(function AOInputContainer(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$d : _b,
      _c = _a.width,
      width = _c === void 0 ? 8 : _c,
      action = _a.action,
      state = _a.state,
      disabled = _a.disabled,
      props = __rest(_a, ["children", "className", "as", "width", "action", "state", "disabled"]);

  var _d = getInputTheme(state, disabled),
      border = _d.border,
      background = _d.background,
      color = _d.color;

  var renderProps = {
    inputProps: {
      seamless: true,
      fullWidth: true,
      className: classnames(!action && "pr-".concat(width)),
      state: state,
      disabled: disabled
    },
    iconProps: {
      className: classnames('flex items-center justify-center', "w-".concat(width), color, {
        'pointer-events-none absolute inset-y-0 right-0': !action,
        'border-0 rounded-tl-none rounded-bl-none': action
      })
    }
  };
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "input-container"
  }, props, {
    className: classnames('flex relative rounded border border-solid focus-within:shadow-outline-blue', border, background, className),
    ref: ref
  }), children(renderProps));
});

var DEFAULT_ELEMENT$c = 'div';
var InputMessage = /*#__PURE__*/React.forwardRef(function AOInputMessage(_a, ref) {
  var children = _a.children,
      className = _a.className,
      _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$c : _b,
      state = _a.state,
      props = __rest(_a, ["children", "className", "as", "state"]);

  var color = {
    'text-tertiary': typeof state === 'undefined',
    'text-success': state === 'success',
    'text-attention': state === 'attention',
    'text-error': state === 'error',
    'text-highlight': state === 'highlight'
  };
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    className: classnames('text-body-sm', color, className),
    "aria-live": "assertive",
    "aria-atomic": "true",
    "data-aods": "input-message"
  }, props, {
    ref: ref
  }), children);
});

var DEFAULT_ELEMENT$b = 'label';
var Label = /*#__PURE__*/React.forwardRef(function AOLabel(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$b : _b,
      children = _a.children,
      className = _a.className,
      srOnly = _a.srOnly,
      inheritTextStyle = _a.inheritTextStyle,
      props = __rest(_a, ["as", "children", "className", "srOnly", "inheritTextStyle"]);

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "label"
  }, props, {
    className: srOnly ? 'sr-only' : classnames(!inheritTextStyle && 'text-body', className),
    ref: ref
  }), children);
});

var DEFAULT_ELEMENT$a = 'div';
var MESSAGE_POSITION_CLASS = {
  left: 'flex-row-reverse space-x-4 space-x-reverse',
  right: 'space-x-4',
  top: 'flex-col-reverse space-y-4 space-y-reverse',
  bottom: 'flex-col space-y-4'
};
var MAIN_COLORS = {
  white: 'text-white',
  dark: 'text-water'
};
var CONTRAST_COLORS = {
  white: 'text-gray-50',
  dark: 'text-steam'
};
var LoadingSpinner = /*#__PURE__*/React.forwardRef(function AOLoadingSpinner(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$a : _b,
      className = _a.className,
      _c = _a.small,
      small = _c === void 0 ? false : _c,
      message = _a.message,
      _d = _a.icon,
      icon = _d === void 0 ? 'ao-logo' : _d,
      _e = _a.inheritTextStyle,
      inheritTextStyle = _e === void 0 ? false : _e,
      _f = _a.messagePosition,
      messagePosition = _f === void 0 ? 'bottom' : _f,
      variant = _a.variant,
      _g = _a.inline,
      inline = _g === void 0 ? false : _g,
      _h = _a.hideIcon,
      hideIcon = _h === void 0 ? false : _h,
      props = __rest(_a, ["as", "className", "small", "message", "icon", "inheritTextStyle", "messagePosition", "variant", "inline", "hideIcon"]);

  var contrastColor = variant ? CONTRAST_COLORS[variant] : 'text-lightgreen';
  var mainColor = variant ? MAIN_COLORS[variant] : 'text-ongreen';
  var messageColor = variant ? MAIN_COLORS[variant] : 'text-secondary';
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "loading-spinner",
    id: "loading-spinner"
  }, props, {
    className: classnames(inline ? 'inline-flex' : 'flex items-center justify-center', MESSAGE_POSITION_CLASS[messagePosition], className),
    ref: ref
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames('relative flex items-center justify-center', small ? 'h-8 w-8' : 'h-16 w-16')
  }, /*#__PURE__*/React.createElement("svg", {
    className: "absolute animate-spin h-full w-full overflow-visible",
    fill: "none",
    viewBox: "0 0 100 100"
  }, /*#__PURE__*/React.createElement("circle", {
    className: contrastColor,
    cx: "50",
    cy: "50",
    r: "50",
    strokeWidth: small ? '8' : '12',
    stroke: "currentColor"
  }), /*#__PURE__*/React.createElement("circle", {
    className: mainColor,
    cx: "50",
    cy: "50",
    r: "50",
    strokeWidth: small ? '8' : '12',
    stroke: "currentColor",
    strokeDasharray: "150%"
  })), !hideIcon && /*#__PURE__*/React.createElement(AoIcon, {
    icon: icon,
    className: mainColor,
    "aria-hidden": "true",
    size: small ? 'lg' : '2x'
  })), /*#__PURE__*/React.createElement("div", {
    className: classnames(!inheritTextStyle && ['text-body', messageColor], !message && 'sr-only'),
    role: "alert",
    "aria-live": "assertive"
  }, message || 'Loading, please wait.'));
});

var defaultStrengthConfig = {
  0: 'error',
  33: 'attention',
  60: 'success'
};
function getThresholdByPercent(strength, strengthConfig) {
  if (strengthConfig === void 0) {
    strengthConfig = defaultStrengthConfig;
  }

  var key = Object.keys(strengthConfig).sort(function (a, b) {
    return Number(b) - Number(a);
  }).find(function (percent) {
    return strength >= Number(percent);
  });
  return key ? strengthConfig[Number(key)] : undefined;
}
var DEFAULT_ELEMENT$9 = 'input';
var PasswordInput = /*#__PURE__*/React.forwardRef(function AOPasswordInput(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$9 : _b,
      className = _a.className,
      id = _a.id,
      _c = _a.showStrength,
      showStrength = _c === void 0 ? false : _c,
      _d = _a.strength,
      strength = _d === void 0 ? 0 : _d,
      strengthConfig = _a.strengthConfig,
      isDisabled = _a["disabled"],
      ariaDescribedby = _a["aria-describedby"],
      state = _a.state,
      props = __rest(_a, ["as", "className", "id", "showStrength", "strength", "strengthConfig", 'disabled', 'aria-describedby', "state"]);

  var _e = useState(false),
      showPassword = _e[0],
      setShowPassword = _e[1];

  var strengthState = getThresholdByPercent(strength, strengthConfig);
  var indicatorBackground = {
    // TODO: Look at the classes for these colours
    'bg-gray-40': isDisabled,
    'bg-ongreen': !isDisabled && strengthState === 'success',
    'bg-rating': !isDisabled && strengthState === 'attention',
    'bg-deals': !isDisabled && strengthState === 'error'
  };
  var indicatorBorder = {
    'border-gray-40': isDisabled || !state,
    'border-success': !isDisabled && state === 'success',
    'border-attention': !isDisabled && state === 'attention',
    'border-error': !isDisabled && state === 'error',
    'border-highlight': !isDisabled && state === 'highlight'
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classnames('inline-block rounded focus-within:shadow-outline-blue', className)
  }, /*#__PURE__*/React.createElement(InputContainer, {
    action: !isDisabled ? true : undefined,
    state: state,
    disabled: isDisabled,
    className: classnames('focus-within:shadow-none', {
      'rounded-b-none': showStrength
    })
  }, function (_a) {
    var inputProps = _a.inputProps,
        iconProps = _a.iconProps;
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Input, __assign({
      autoComplete: showStrength ? 'new-password' : 'current-password',
      "data-aods": "password-input"
    }, props, inputProps, {
      as: as,
      id: id,
      ref: ref,
      type: showPassword ? 'text' : 'password',
      "aria-describedby": showStrength || ariaDescribedby ? [showStrength && "".concat(id, "-strength-message"), ariaDescribedby].filter(Boolean).join(' ') : undefined
    })), !isDisabled && /*#__PURE__*/React.createElement(AoIcon, __assign({}, iconProps, {
      as: "button",
      type: "button",
      "aria-controls": id,
      className: classnames(iconProps.className, 'bg-transparent border-none cursor-pointer rounded-tr focus:shadow-outline-blue focus:outline-none text-secondary', {
        'rounded-br': !showStrength
      }),
      icon: showPassword ? 'hide' : 'show',
      label: showPassword ? 'Hide password' : 'Show password',
      onClick: function onClick() {
        return setShowPassword(function (isVisible) {
          return !isVisible;
        });
      }
    })));
  }), showStrength && /*#__PURE__*/React.createElement("div", {
    className: classnames('w-full overflow-hidden border border-t-0 border-solid rounded-b', indicatorBorder)
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames('h-2', indicatorBackground),
    style: {
      width: "".concat(strength, "%")
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only",
    id: "".concat(id, "-strength-message"),
    "aria-live": "polite",
    "aria-atomic": true
  }, "Password strength ", strength, "%"))));
});

var RadioButton = /*#__PURE__*/React.forwardRef(function AORadioButton(props, ref) {
  return /*#__PURE__*/React.createElement(ToggleBase, __assign({}, props, {
    type: "radio-button",
    ref: ref
  }));
});

var RadioButtonGroup = function RadioButtonGroup(_a) {
  var children = _a.children,
      props = __rest(_a, ["children"]);

  return /*#__PURE__*/React.createElement(ToggleBaseGroup, __assign({}, props), children);
};

var DEFAULT_ELEMENT$8 = 'select';
var Select = /*#__PURE__*/React.forwardRef(function AOSelect(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$8 : _b,
      children = _a.children,
      className = _a.className,
      state = _a.state,
      disabled = _a.disabled,
      props = __rest(_a, ["as", "children", "className", "state", "disabled"]);

  return /*#__PURE__*/React.createElement(InputContainer, {
    className: "bg-white",
    state: state,
    disabled: disabled,
    "data-aods": "select"
  }, function (_a) {
    var inputProps = _a.inputProps,
        iconProps = _a.iconProps;
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Input, __assign({}, props, inputProps, {
      as: as,
      className: classnames('appearance-none', inputProps.className, className),
      ref: ref
    }), children), /*#__PURE__*/React.createElement(AoIcon, __assign({}, iconProps, {
      icon: "chevron-down"
    })));
  });
});

var SummaryCard = /*#__PURE__*/React.forwardRef(function AOSummaryCard(_a, ref) {
  var children = _a.children,
      className = _a.className,
      inheritTextStyle = _a.inheritTextStyle,
      buttonText = _a.buttonText,
      _b = _a.buttonProps,
      buttonProps = _b === void 0 ? {} : _b,
      onButtonClick = _a.onButtonClick,
      props = __rest(_a, ["children", "className", "inheritTextStyle", "buttonText", "buttonProps", "onButtonClick"]);

  return /*#__PURE__*/React.createElement(Card, __assign({
    "data-aods": "summary-card"
  }, props, {
    className: className,
    ref: ref
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames(!inheritTextStyle && 'text-body-sm', 'flex justify-between')
  }, children), buttonText && /*#__PURE__*/React.createElement(Button, __assign({}, buttonProps, {
    variant: "link",
    onClick: onButtonClick
  }), buttonText));
});

var DEFAULT_ELEMENT$7 = 'button';
var Tab = /*#__PURE__*/React.forwardRef(function AOTab(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$7 : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      groupName = _a.groupName,
      _d = _a.inheritTextStyle,
      inheritTextStyle = _d === void 0 ? false : _d,
      tabId = _a.tabId,
      props = __rest(_a, ["as", "children", "className", "isOpen", "groupName", "inheritTextStyle", "tabId"]);

  if (!groupName || tabId === undefined) {
    // Don't render if props required for accessible rendering aren't available
    return null;
  }

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "tab"
  }, props, {
    ref: ref,
    className: classnames(className, !inheritTextStyle && 'text-title', isOpen ? 'border-b-2 border-gray-40' : 'border-0', 'bg-transparent w-full flex items-center justify-between text-left p-4 focus:shadow-outline-blue focus:outline-none '),
    type: "button",
    tabIndex: isOpen ? 0 : -1,
    "aria-selected": isOpen,
    "aria-controls": "".concat(groupName, "-content-").concat(tabId),
    role: "tab",
    "test-id": groupName
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center flex-grow"
  }, children));
});

var DEFAULT_ELEMENT$6 = 'div';
var TabContent = /*#__PURE__*/React.forwardRef(function AOTabContent(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$6 : _b,
      activeTab = _a.activeTab,
      children = _a.children,
      className = _a.className,
      groupName = _a.groupName,
      props = __rest(_a, ["as", "activeTab", "children", "className", "groupName"]);

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "tab-content"
  }, filterProps(props, ['onClick']), {
    className: classnames(className),
    ref: ref
  }), React.Children.map(children, function (child, index) {
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.isValidElement(child) ? /*#__PURE__*/React.cloneElement(child, {
      isOpen: activeTab === index,
      tabId: index,
      groupName: groupName
    }) : child);
  }));
});

var DEFAULT_ELEMENT$5 = 'ul';
var PREV_KEY = 'ArrowLeft';
var NEXT_KEY = 'ArrowRight';
var TabHeader = /*#__PURE__*/React.forwardRef(function AOTabHeader(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$5 : _b,
      children = _a.children,
      className = _a.className,
      groupName = _a.groupName,
      onClick = _a.onClick,
      label = _a.label,
      activeTab = _a.activeTab,
      props = __rest(_a, ["as", "children", "className", "groupName", "onClick", "label", "activeTab"]);

  var tabRefs = [];
  React.Children.forEach(children, function () {
    tabRefs.push( /*#__PURE__*/React.createRef());
  });
  var createClickHandler = useCallback(function (tabId) {
    return function (e) {
      return onClick === null || onClick === void 0 ? void 0 : onClick(e, tabId);
    };
  }, [onClick]);

  var handleTabSelect = function handleTabSelect(next) {
    var _a, _b;

    if (activeTab === undefined) {
      return;
    }

    var newActiveTab = activeTab + (next ? 1 : -1);

    if (newActiveTab < 0 || newActiveTab > tabRefs.length - 1) {
      return;
    }

    onClick === null || onClick === void 0 ? void 0 : onClick(null, newActiveTab);
    (_b = (_a = tabRefs[newActiveTab]) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
  };

  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === PREV_KEY) {
      handleTabSelect(false);
    } else if (e.key === NEXT_KEY) {
      handleTabSelect(true);
    }
  };

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "tab-header",
    role: "tablist",
    "aria-label": label
  }, props, {
    className: classnames('border border-l-0 border-r-0 border-t-0 border-gray-40', className),
    ref: ref
  }), React.Children.map(children, function (child, index) {
    return /*#__PURE__*/React.createElement("li", {
      role: "presentation"
    }, /*#__PURE__*/React.isValidElement(child) ? /*#__PURE__*/React.cloneElement(child, {
      onClick: createClickHandler(index),
      onKeyDown: handleKeyDown,
      ref: tabRefs[index],
      isOpen: activeTab === index,
      tabId: index,
      groupName: groupName
    }) : child);
  }));
});

var DEFAULT_ELEMENT$4 = 'div';
var TabPanel = /*#__PURE__*/React.forwardRef(function AOTabPanel(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$4 : _b,
      children = _a.children,
      className = _a.className,
      groupName = _a.groupName,
      _c = _a.isOpen,
      isOpen = _c === void 0 ? false : _c,
      _d = _a.inheritTextStyle,
      inheritTextStyle = _d === void 0 ? false : _d,
      tabId = _a.tabId,
      _e = _a.noPadding,
      noPadding = _e === void 0 ? false : _e,
      props = __rest(_a, ["as", "children", "className", "groupName", "isOpen", "inheritTextStyle", "tabId", "noPadding"]);

  if (!groupName || tabId === undefined) {
    // Don't render if props required for accessible rendering aren't available
    return null;
  }

  return /*#__PURE__*/React.createElement(ElementType, __assign({
    id: "".concat(groupName, "-panel-").concat(tabId),
    className: classnames(!isOpen && 'hidden', !inheritTextStyle && 'text-body', !noPadding && 'p-4', className),
    "data-aods": "tab-panel",
    role: "tabpanel",
    "aria-labelledby": groupName,
    tabIndex: isOpen ? 0 : -1
  }, props, {
    ref: ref
  }), children);
});

var DEFAULT_ELEMENT$3 = 'section';
var Tabs = /*#__PURE__*/React.forwardRef(function AOTab(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$3 : _b,
      children = _a.children,
      id = _a.id,
      props = __rest(_a, ["as", "children", "id"]);

  var _c = useState(0),
      activeTab = _c[0],
      setActiveTab = _c[1];

  var handleTabClick = useCallback(function (_e, tabId) {
    return setActiveTab(function () {
      return tabId;
    });
  }, []);
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "tab",
    id: id
  }, props, {
    ref: ref
  }), React.Children.map(children, function (child) {
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.isValidElement(child) ? /*#__PURE__*/React.cloneElement(child, {
      onClick: handleTabClick,
      activeTab: activeTab,
      groupName: id
    }) : child);
  }));
});

var DEFAULT_ELEMENT$2 = 'textarea';
var Textarea = /*#__PURE__*/React.forwardRef(function AOTextarea(_a, ref) {
  var _b = _a.as,
      as = _b === void 0 ? DEFAULT_ELEMENT$2 : _b,
      props = __rest(_a, ["as"]);

  return /*#__PURE__*/React.createElement(Input, __assign({
    "data-aods": "textarea"
  }, props, {
    as: as,
    ref: ref
  }));
});

function useFocusAway(ref, onFocusAway) {
  useEffect(function () {
    if (!document || !ref.current || !onFocusAway) return;

    function handleFocusOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        onFocusAway === null || onFocusAway === void 0 ? void 0 : onFocusAway(event);
      }
    }

    document.addEventListener('focusin', handleFocusOutside); // eslint-disable-next-line consistent-return

    return function () {
      document.removeEventListener('focusin', handleFocusOutside);
    };
  }, [ref, onFocusAway]);
}

var translations = {
  en: {
    close: function close() {
      return 'Close';
    }
  },
  de: {
    close: function close() {
      return 'Schließen';
    }
  }
};

var DEFAULT_ELEMENT$1 = 'div';
var TOOLTIP_OFFSET = 4;
var ARROW_SIZE = 8;
var ARROW_OFFSET = 4; // Prevent it from being placed against rounded corner

var tooltipPositions = ['top', 'bottom', 'left', 'right'];
var tooltipPositionToArrowDirection = {
  top: 'down',
  bottom: 'up',
  left: 'right',
  right: 'left'
};

var getArrowDirection = function getArrowDirection(popperAttributes) {
  var _a;

  var arrowDirection = '';
  var placement = (_a = popperAttributes === null || popperAttributes === void 0 ? void 0 : popperAttributes.popper) === null || _a === void 0 ? void 0 : _a['data-popper-placement'];
  if (!placement) return arrowDirection;
  tooltipPositions.forEach(function (position) {
    if (placement.startsWith(position)) {
      arrowDirection = tooltipPositionToArrowDirection[position];
    }
  });
  return arrowDirection;
}; // TODO: arrow currently doesn't have a shadow due to being CSS shape, replace with rotated square


var Tooltip = /*#__PURE__*/React.forwardRef(function AOTooltip(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT$1 : _b,
      children = _a.children,
      className = _a.className,
      _c = _a.placement,
      placement = _c === void 0 ? 'auto' : _c,
      _d = _a.referenceElement,
      referenceElement = _d === void 0 ? null : _d,
      hideArrow = _a.hideArrow,
      isOpen = _a.isOpen,
      hideCloseButton = _a.hideCloseButton,
      onClose = _a.onClose,
      onClickAway = _a.onClickAway,
      title = _a.title,
      _e = _a.maxWidth,
      maxWidth = _e === void 0 ? 'xs' : _e,
      props = __rest(_a, ["as", "children", "className", "placement", "referenceElement", "hideArrow", "isOpen", "hideCloseButton", "onClose", "onClickAway", "title", "maxWidth"]);

  var language = useConfig().language;
  var t = useTranslation(language, translations);
  var tooltipElement = useForwardedRef(ref);

  var _f = useState(null),
      arrowElement = _f[0],
      setArrowElement = _f[1];

  var _g = useState(null),
      arrowBorderElement = _g[0],
      setArrowBorderElement = _g[1];

  var _h = usePopper(referenceElement, tooltipElement.current, {
    placement: placement,
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, TOOLTIP_OFFSET + ARROW_SIZE]
      }
    }, {
      name: 'arrow',
      options: {
        element: arrowElement,
        padding: ARROW_OFFSET + 1
      }
    }, {
      name: 'arrow',
      options: {
        element: arrowBorderElement,
        padding: ARROW_OFFSET
      }
    }]
  }),
      styles = _h.styles,
      attributes = _h.attributes;

  var arrowDirection = useMemo(function () {
    return getArrowDirection(attributes);
  }, [attributes]);
  useClickAway(tooltipElement, isOpen ? onClickAway || onClose : undefined);
  useFocusAway(tooltipElement, isOpen ? onClickAway || onClose : undefined);
  useKeyDown({
    isEnabled: isOpen,
    key: 'Escape',
    onKeyDown: onClose
  });
  return /*#__PURE__*/React.createElement("span", {
    role: "status"
  }, /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "tooltip"
  }, props, {
    className: classnames('text-body bg-white border border-solid border-gray-40 rounded transition-opacity duration-200 shadow-2dp', isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none', maxWidth !== 'none' && "max-w-".concat(maxWidth), className),
    role: "tooltip",
    ref: tooltipElement,
    style: styles.popper
  }, attributes.popper), (title || !hideCloseButton) && /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-between pl-4"
  }, /*#__PURE__*/React.isValidElement(title) && /*#__PURE__*/React.cloneElement(title, {
    className: classnames('m-0', title.props.className)
  }), !hideCloseButton && /*#__PURE__*/React.createElement(Button, {
    className: "ml-auto",
    variant: "tertiary",
    icon: true,
    onClick: onClose,
    tabIndex: isOpen ? undefined : -1,
    "aria-label": t('close')
  }, /*#__PURE__*/React.createElement(AoIcon, {
    icon: "close",
    size: "xl"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "p-4"
  }, children), !hideArrow && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classnames('arrow triangle triangle-sm triangle-offset text-gray-40', "triangle-".concat(arrowDirection)),
    ref: setArrowBorderElement,
    style: styles.arrow
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames('arrow triangle triangle-bordered triangle-sm triangle-offset text-white', (arrowDirection === 'left' || arrowDirection === 'right') && 'mt-px', (arrowDirection === 'up' || arrowDirection === 'down') && 'ml-px', "triangle-".concat(arrowDirection)),
    ref: setArrowElement,
    style: styles.arrow
  }))));
});

var DEFAULT_ELEMENT = 'div';
var TooltipManager = /*#__PURE__*/React.forwardRef(function AOTooltipManager(_a, ref) {
  var _b = _a.as,
      ElementType = _b === void 0 ? DEFAULT_ELEMENT : _b,
      children = _a.children,
      id = _a.id,
      _c = _a.defaultOpen,
      defaultOpen = _c === void 0 ? false : _c,
      _d = _a.openDelay,
      openDelay = _d === void 0 ? 0 : _d,
      _e = _a.closeDelay,
      closeDelay = _e === void 0 ? 1500 : _e,
      _f = _a.referenceTriggers,
      referenceTriggers = _f === void 0 ? ['click', 'focus', 'hover'] : _f,
      props = __rest(_a, ["as", "children", "id", "defaultOpen", "openDelay", "closeDelay", "referenceTriggers"]);

  var _g = useState(null),
      referenceElement = _g[0],
      setReferenceElement = _g[1];

  var _h = useState(defaultOpen),
      isOpen = _h[0],
      setOpen = _h[1];

  var enterTimer = React.useRef();
  var leaveTimer = React.useRef();
  var tooltipId = "".concat(id, "-tooltip");

  var handleOpen = function handleOpen() {
    setOpen(true);
    window.clearTimeout(enterTimer.current);
  };

  var handleClose = function handleClose() {
    setOpen(false);
  };

  var handleEnter = function handleEnter() {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
    enterTimer.current = window.setTimeout(function () {
      handleOpen();
    }, openDelay);
  };

  var handleLeave = function handleLeave() {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(function () {
      handleClose();
    }, closeDelay);
  };

  var handleTooltipEnter = function handleTooltipEnter() {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
  };

  var renderProps = {
    referenceProps: __assign({
      'aria-describedby': tooltipId,
      'ref': setReferenceElement
    }, referenceTriggers.reduce(function (triggerProps, trigger) {
      switch (trigger) {
        case 'click':
          return __assign(__assign({}, triggerProps), {
            onClick: handleOpen
          });

        case 'focus':
          return __assign(__assign({}, triggerProps), {
            onFocus: handleEnter,
            onBlur: handleLeave
          });

        case 'hover':
          return __assign(__assign({}, triggerProps), {
            onMouseEnter: handleEnter,
            onMouseLeave: handleLeave
          });

        default:
          return triggerProps;
      }
    }, {})),
    tooltipProps: {
      id: tooltipId,
      referenceElement: referenceElement,
      isOpen: isOpen,
      onClose: handleClose,
      onClickAway: handleLeave,
      onFocus: handleTooltipEnter,
      onMouseOver: handleTooltipEnter
    },
    isOpen: isOpen,
    openTooltip: handleOpen,
    closeTooltip: handleClose,
    tabIndex: isOpen ? undefined : -1
  };
  return /*#__PURE__*/React.createElement(ElementType, __assign({
    "data-aods": "tooltip-manager"
  }, props, {
    ref: ref
  }), children(renderProps));
});

export { Accordion, AccordionContent, AccordionHeader, AccordionItem, Breadcrumb, BreadcrumbItem, Button, Card, CardDivider, Checkbox, CheckboxGroup, Container, DesignSystemProvider, Drawer, EnergyLabel, Field, FieldSet, Grid, GridItem, AoIcon as Icon, Input, InputContainer, InputMessage, Label, Legend, LoadingSpinner, Modal, PasswordInput, RadioButton, RadioButtonGroup, Select, SummaryCard, Tab, TabContent, TabHeader, TabPanel, Tabs, Tag, Textarea, Tooltip, TooltipManager };
//# sourceMappingURL=components-react.esm.js.map
